/**
 * Locale-aware formatting helpers used by notification rendering.
 *
 * Kept tiny and dependency-free so the notification components don't pull in
 * heavy date libraries. The browser's Intl API covers everything we need for
 * nl/en/de/fr.
 */

const SUPPORTED_LOCALES = ['nl', 'en', 'de', 'fr'];

export function resolveLocale(locale) {
  if (!locale) return 'nl';
  const normalised = String(locale).toLowerCase();
  if (SUPPORTED_LOCALES.includes(normalised)) return normalised;
  const primary = normalised.split('-')[0];
  return SUPPORTED_LOCALES.includes(primary) ? primary : 'nl';
}

export function formatDate(value, locale = 'nl', opts = { dateStyle: 'medium' }) {
  if (value == null) return '';
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat(resolveLocale(locale), opts).format(d);
  } catch {
    return String(value);
  }
}

export function formatRelativeTime(value, locale = 'nl', referenceDate = new Date()) {
  if (value == null) return '';
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const diffSeconds = Math.round((d.getTime() - referenceDate.getTime()) / 1000);
    const rtf = new Intl.RelativeTimeFormat(resolveLocale(locale), { numeric: 'auto' });
    const units = [
      [60, 'second'],
      [3600, 'minute'],
      [86400, 'hour'],
      [86400 * 7, 'day'],
      [86400 * 30, 'week'],
      [86400 * 365, 'month'],
      [Infinity, 'year'],
    ];
    const abs = Math.abs(diffSeconds);
    let prev = 1;
    for (const [bound, unit] of units) {
      if (abs < bound) {
        const factor = unit === 'second' ? 1 : prev;
        return rtf.format(Math.round(diffSeconds / factor), unit);
      }
      prev = bound;
    }
    return rtf.format(Math.round(diffSeconds / (86400 * 365)), 'year');
  } catch {
    return String(value);
  }
}

export function formatNumber(value, locale = 'nl', opts = {}) {
  if (value == null || value === '') return '';
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(n)) return String(value);
  try {
    // `negative` drops the sign of -0 and of anything that rounds to zero at
    // the requested precision, so a zero cost reads "€ 0", never "€ -0,00".
    // A caller that wants a sign on zero passes its own signDisplay.
    return new Intl.NumberFormat(resolveLocale(locale), {
      signDisplay: 'negative',
      ...opts,
    }).format(n);
  } catch {
    return String(value);
  }
}

export function formatCurrency(value, currency = 'EUR', locale = 'nl') {
  return formatNumber(value, locale, { style: 'currency', currency, maximumFractionDigits: 2 });
}

export function formatDuration(seconds, locale = 'nl') {
  if (seconds == null || seconds === '') return '';
  const total = Math.max(0, Math.floor(Number(seconds)));
  if (Number.isNaN(total)) return '';
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  // Localise the unit suffixes via Intl.RelativeTimeFormat-style fallbacks — keep it terse.
  const isNl = resolveLocale(locale) === 'nl';
  if (h > 0) return isNl ? `${h}u ${m}m` : `${h}h ${m}m`;
  if (m > 0) return isNl ? `${m}m ${s}s` : `${m}m ${s}s`;
  return `${s}s`;
}
